import { useState, useEffect } from 'react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { clearSilentSsoFailedFlag } from '../utils/silentSso';

export const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (authService.isAuthenticated()) {
          const profileData = await authService.getProfile();
          if (!cancelled) setUser(profileData);
          return;
        }
        // Connexion UnWee : cookie httpOnly, pas de jeton dans localStorage.
        const response = await apiClient.get('/auth/session');
        if (!cancelled) setUser(response.data);
      } catch (error) {
        console.error('useAuth - session introuvable', error);
        if (authService.isAuthenticated()) {
          authService.logout();
        }
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const authResponse = await authService.login(email, password);
      setUser(authResponse.user);
      return authResponse;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    void (async () => {
      let logoutUrl: string | undefined;
      try {
        const result = await apiClient.post('/auth/sso/logout');
        logoutUrl = result.data?.logoutUrl || undefined;
      } catch {
        /* session déjà absente */
      }
      authService.logout();
      clearSilentSsoFailedFlag();
      setUser(null);
      if (logoutUrl) {
        window.location.assign(logoutUrl);
        return;
      }
      window.location.assign('/login');
    })();
  };

  const updateProfile = async (profileData: any) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (error) {
      throw error;
    }
  };

  return {
    user,
    isLoading,
    login,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
  };
};
